import { errorView, type QueryResultLike } from '@krp/client-runtime'

import { STEP_TITLES } from './scanText'
import type { SelectedCard } from './selectionSlice'
import {
  PAGE_SIZE,
  type DuplicatesVm,
  type ExportView,
  type PairCounters,
  type PairView,
  type PairsPage,
  type Scan,
  type ScanError,
  type ScanStatus,
  type ScanVm,
  type ShownError,
} from './types'

const NO_COUNTERS: PairCounters = { all: 0, undecided: 0, marked: 0, notDuplicate: 0 }

/** Текст для передачи администратору: заголовок, дословный текст ответа, код и идентификатор запроса (О-1). */
function reportOf(title: string, message: string, code: string, requestId: string | null): string {
  return [title, message, `Код: ${code}`, requestId === null ? null : `Запрос: ${requestId}`].filter((line) => line !== null).join('\n')
}

/** Отказ запроса или команды: текст службы дословно, код и идентификатор запроса — рядом. */
export function shownError(title: string, error: unknown): ShownError {
  const view = errorView(error)
  return { title, message: view.message, code: view.code, requestId: view.correlationId, report: reportOf(title, view.message, view.code, view.correlationId) }
}

/** Ошибка шага проверки (`DuplicateScanErrorDto`): шаг, дословный текст, код, идентификатор запроса. */
export function scanErrorShown(error: ScanError): ShownError {
  const title = `Проверка прервалась на шаге «${STEP_TITLES[error.step]}»`
  return { title, message: error.message, code: error.code, requestId: error.requestId, report: reportOf(title, error.message, error.code, error.requestId) }
}

export interface DuplicatesVmInput {
  resourceId: string
  filter: DuplicatesVm['filter']
  page: number
  result: QueryResultLike<PairsPage>
  selected: SelectedCard | null
  busyPairs: ReadonlySet<number>
  pairErrors: ReadonlyMap<number, ShownError>
  exportView: ExportView
}

/** Что рисует список пар: страница из кэша запроса, выбранная карточка из слайса, ход команд и отказы у пар. */
export function buildDuplicatesVm(input: DuplicatesVmInput): DuplicatesVm {
  const data = input.result.data
  const pageSize = data?.pageSize ?? PAGE_SIZE
  const total = data?.total ?? 0
  const pairs: PairView[] = (data?.items ?? []).map((pair) => ({
    pair,
    selectedKey:
      input.selected !== null && input.selected.resourceId === input.resourceId && input.selected.pairId === pair.id ? input.selected.key : null,
    busy: input.busyPairs.has(pair.id),
    error: input.pairErrors.get(pair.id) ?? null,
  }))
  return {
    resourceId: input.resourceId,
    filter: input.filter,
    page: input.page,
    pages: Math.max(1, Math.ceil(total / pageSize)),
    status: input.result.isError ? 'failed' : data === undefined ? 'loading' : 'ready',
    loadError: input.result.isError ? shownError('Не удалось получить список пар', input.result.error) : null,
    refreshing: data !== undefined && input.result.isLoading,
    total,
    counters: data?.counters ?? NO_COUNTERS,
    hasScan: data !== undefined && data.scanId !== null,
    pairs,
    exportView: input.exportView,
  }
}

export interface ScanVmInput {
  result: QueryResultLike<ScanStatus>
  busy: boolean
  commandError: ShownError | null
}

/** Проверка источника: ответ `GET …/scan`, ход команды над ней и её отказ (запуск, остановка, продолжение). */
export function buildScanVm(input: ScanVmInput): ScanVm {
  const scan: Scan | null = input.result.data?.scan ?? null
  return {
    loading: !input.result.isError && input.result.data === undefined,
    loadError: input.result.isError ? shownError('Не удалось узнать ход проверки', input.result.error) : null,
    scan,
    busy: input.busy,
    commandError: input.commandError,
  }
}
